import type { ADNode } from '@atlaskit/editor-common/utils/validator';

export type MacroName = string;

// Note: Please also update next/packages/content-renderer/src/ssr/getSSRExtensionHandlers.tsx whenever the type here updates.
export const EXTENSION_TYPE = {
	TEMPLATE: 'com.atlassian.confluence.template',
	MACRO_CORE: 'com.atlassian.confluence.macro.core',
	MIGRATION: 'com.atlassian.confluence.migration',
	// used for all macros that use Extension API and require references to be rendered
	MACRO_WITH_REFERENCES: 'com.atlassian.confluence.macro.with.references',
	NATIVE: 'com.atlassian.confluence.native',
};

export const MIGRATION_EXTENSION_KEYS = {
	INVALID: '__confluenceADFMigrationUnsupportedContentInternalExtension__',
};

export const FALLBACK_REASONS = {
	UNSUPPORTED_EXTENSION: 'unsupportedExtension',
	INVALID_ADF: 'invalidAdf',
};

export const REGULAR_EXTENSION_TYPE = 'extension';
export const INLINE_EXTENSION_TYPE = 'inlineExtension';
export const BODIED_EXTENSION_TYPE = 'bodiedExtension';
export const MULTI_BODIED_EXTENSION_TYPE = 'multiBodiedExtension';

export const EMPTY_ADF_CONTENT: ADNode[] = [{ content: [], type: 'paragraph' }];
export const MULTI_BODIED_INITIAL_CONTENT: ADNode[] = [
	{
		type: 'extensionFrame',
		content: [
			{
				type: 'paragraph',
				content: [],
			},
		],
	},
];

export const EXTENSION_KEY = {
	TEMPLATE: 'variable',
	INLINE_EXTERNAL_IMAGE: 'inline-external-image',
	INLINE_MEDIA_IMAGE: 'inline-media-image',
	PRIVACY_MARK: 'privacy-mark',
	ANCHOR: 'anchor',
	PANEL: 'panel',
	SECTION: 'section',
	CHEESE: 'cheese',
	COLUMN: 'column',
	CONTENT_BY_LABEL: 'contentbylabel',
	CONTENT_BY_USER: 'content-by-user',
	CREATE_SPACE_BUTTON: 'create-space-button',
	FAVORITE_PAGES: 'favpages',
	GLOBAL_REPORTS: 'global-reports',
	IM_PRESENCE: 'im',
	LOREMIPSUM: 'loremipsum',
	NAVIGATION_MAP: 'navmap',
	PAGE_TREE: 'pagetree',
	RECENTLY_UPDATED: 'recently-updated',
	RECENTLY_USED_LABELS: 'recently-used-labels',
	SEARCH: 'search',
	SPACE_DETAILS: 'space-details',
	INVALID: '__confluenceADFMigrationUnsupportedContentInternalExtension__',
	REDACTED_MACRO_PLACEHOLDER: 'redactedMacroPlaceholder',
	TOC: 'toc',
	LIVE_SEARCH: 'livesearch',
	CREATE_FROM_TEMPLATE: 'create-from-template',
	CONFLUENCE_DATABASES: 'database-extension',
	DETAILS: 'details',
	DETAILS_SUMMARY: 'detailssummary',
	EXPAND: 'expand',
	EXCERPT: 'excerpt',
	EXCERPT_INCLUDE: 'excerpt-include',
	CHILDREN: 'children',
	PROFILE_PICTURE: 'profile-picture',
	PROFILE: 'profile',
	BLOG_POSTS: 'blog-posts',
	INCLUDE: 'include',
	JIRA: 'jira',
	JIRACHART: 'jirachart',
	LEGACY_CONTENT: 'legacy-content',
	TEAM_CALENDARS: 'calendar',
	CONTRIBUTORS: 'contributors',
	IFRAME: 'iframe',
	POPULAR_LABELS: 'popular-labels',
	TASKS_REPORT_MACRO: 'tasks-report-macro',
	NATIVE_EMBED: 'native-embed',
	HIDE: 'hide',
	PAGE_TREE_SEARCH: 'pagetreesearch',
	WIDGET: 'widget',
	CONTENT_REPORT_TABLE: 'content-report-table',
	CHANGE_HISTORY: 'change-history',
	VIEWPDF: 'viewpdf',
	ATTACHMENTS: 'attachments',
	AGENT_MANAGED_BLOCK: 'agent-managed-block',
};

export const allowedMacrosForInsertMenu: MacroName[] = [
	'toc',
	'expand',
	'trello-board',
	'status',
	'jiraroadmap',
];

export const serverRenderedExtensions: MacroName[] = [
	EXTENSION_KEY.TOC,
	EXTENSION_KEY.ANCHOR,
	EXTENSION_KEY.LIVE_SEARCH,
	EXTENSION_KEY.DETAILS_SUMMARY,
	EXTENSION_KEY.DETAILS,
	EXTENSION_KEY.EXCERPT,
	EXTENSION_KEY.EXPAND,
	EXTENSION_KEY.EXCERPT_INCLUDE,
	EXTENSION_KEY.CREATE_FROM_TEMPLATE,
	EXTENSION_KEY.REDACTED_MACRO_PLACEHOLDER,
	EXTENSION_KEY.CONFLUENCE_DATABASES,
	EXTENSION_KEY.PROFILE_PICTURE,
	EXTENSION_KEY.PROFILE,
	EXTENSION_KEY.ATTACHMENTS,
	EXTENSION_KEY.BLOG_POSTS,
	EXTENSION_KEY.RECENTLY_UPDATED,
	EXTENSION_KEY.TEAM_CALENDARS,
	EXTENSION_KEY.CONTRIBUTORS,
	EXTENSION_KEY.IFRAME,
	EXTENSION_KEY.POPULAR_LABELS,
	EXTENSION_KEY.CONTENT_BY_LABEL,
	EXTENSION_KEY.PAGE_TREE,
	EXTENSION_KEY.PAGE_TREE_SEARCH,
	EXTENSION_KEY.INCLUDE,
	EXTENSION_KEY.WIDGET,
	EXTENSION_KEY.CONTENT_REPORT_TABLE,
	EXTENSION_KEY.CHANGE_HISTORY,
	EXTENSION_KEY.VIEWPDF,
	EXTENSION_KEY.TASKS_REPORT_MACRO,
];

export const supportedExtensions: Set<string> = new Set([
	REGULAR_EXTENSION_TYPE,
	INLINE_EXTENSION_TYPE,
	BODIED_EXTENSION_TYPE,
]);

// Fragment limited to 255 in backend.  Moving this to frontend (here).
export const MAX_ATLASSIAN_ALLOWED_FRAGMENT_LENGTH = 255;

export const MODERENIZED_MACRO_KEYS: string[] = [
	EXTENSION_KEY.CHILDREN,
	EXTENSION_KEY.EXCERPT,
	EXTENSION_KEY.EXCERPT_INCLUDE,
	EXTENSION_KEY.CONTENT_BY_LABEL,
	EXTENSION_KEY.RECENTLY_UPDATED,
];

export const MACROS_WITH_LIVE_DOC_REFERENCE: MacroName[] = [
	EXTENSION_KEY.CHILDREN,
	EXTENSION_KEY.DETAILS, // Content properties (Page properties) macro
	EXTENSION_KEY.PAGE_TREE,
	EXTENSION_KEY.INCLUDE,
	EXTENSION_KEY.DETAILS_SUMMARY, // Content properties report (Page properties report) macro
];
